// Canvas-rendered "visitor side" stream for agents. Mirrors the widget's
// agent-placeholder, but the labels read "Visitor" and the LIVE state uses
// the visitor's initials. Captured as a MediaStream for an <video> element.
//
// Phase 5 replaces with real getUserMedia of the agent's own camera + a real
// RTCPeerConnection-mediated remote stream from the visitor.

export type Mode = 'waiting' | 'live';

export interface PlaceholderHandle {
  stream: MediaStream;
  stop: () => void;
  setMode: (mode: Mode) => void;
  setVisitor: (name: string) => void;
}

const W = 640;
const H = 360;

export function createVisitorPlaceholder(initial: { mode?: Mode; visitorName?: string } = {}): PlaceholderHandle {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return noopHandle();

  let mode: Mode = initial.mode ?? 'waiting';
  let visitorName = initial.visitorName ?? 'Visitor';
  let rafId = 0;
  const start = performance.now();

  function frame(now: number) {
    if (!ctx) return;
    const t = (now - start) / 1000;
    ctx.fillStyle = '#14110E';
    ctx.fillRect(0, 0, W, H);
    if (mode === 'waiting') drawWaiting(ctx, t);
    else drawLive(ctx, t, visitorName);
    rafId = requestAnimationFrame(frame);
  }
  rafId = requestAnimationFrame(frame);

  const stream =
    typeof canvas.captureStream === 'function' ? canvas.captureStream(15) : new MediaStream();

  return {
    stream,
    stop: () => {
      cancelAnimationFrame(rafId);
      stream.getTracks().forEach((t) => t.stop());
    },
    setMode: (m) => {
      mode = m;
    },
    setVisitor: (name) => {
      visitorName = name;
    },
  };
}

function noopHandle(): PlaceholderHandle {
  const empty = new MediaStream();
  return {
    stream: empty,
    stop: () => {},
    setMode: () => {},
    setVisitor: () => {},
  };
}

function drawWaiting(ctx: CanvasRenderingContext2D, t: number) {
  ctx.strokeStyle = '#2A2520';
  ctx.lineWidth = 1;
  const ruleY = H * 0.18 + Math.sin(t * 0.4) * 4;
  ctx.beginPath();
  ctx.moveTo(40, ruleY);
  ctx.lineTo(W - 40, ruleY);
  ctx.stroke();
  ctx.fillStyle = '#9B9180';
  ctx.font = "500 14px 'Onest', sans-serif";
  ctx.textAlign = 'center';
  ctx.fillText('CONNECTING TO VISITOR…', W / 2, H / 2);
}

function drawLive(ctx: CanvasRenderingContext2D, t: number, name: string) {
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, '#1C1814');
  grad.addColorStop(1, '#14110E');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  const cx = W / 2;
  const cy = H / 2 + 30;
  const bob = Math.sin(t * 1.4) * 2;
  ctx.fillStyle = '#2A2520';
  ctx.beginPath();
  ctx.moveTo(cx - 130, H);
  ctx.bezierCurveTo(cx - 130, cy + 40 + bob, cx - 60, cy + 10 + bob, cx, cy + 10 + bob);
  ctx.bezierCurveTo(cx + 60, cy + 10 + bob, cx + 130, cy + 40 + bob, cx + 130, H);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(cx, cy - 40 + bob, 44, 52, 0, 0, Math.PI * 2);
  ctx.fillStyle = '#3D362E';
  ctx.fill();

  ctx.fillStyle = '#D9522F';
  ctx.font = "600 10px 'Geist Mono', monospace";
  ctx.textAlign = 'left';
  ctx.fillText('● LIVE', 28, 30);

  ctx.fillStyle = '#ECE5D6';
  ctx.font = "500 13px 'Onest', sans-serif";
  ctx.textAlign = 'right';
  ctx.fillText(name.toUpperCase(), W - 28, 30);
}
